import { useState, useEffect, useCallback } from 'react';
import { Heart, Plus, RefreshCw, Calendar, Users, DollarSign, CheckCircle, X } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface CoverageTier {
  tier: 'employee' | 'employee_spouse' | 'family';
  employee_cost: number;
  employer_cost: number;
}

interface BenefitPlan {
  id: string;
  name: string;
  plan_type: string;
  provider: string;
  description?: string;
  coverage_tiers: CoverageTier[];
  active: boolean;
  created_at: string;
}

interface EnrollmentPeriod {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  effective_date: string;
  status: 'upcoming' | 'open' | 'closed';
  completion_count: number;
  eligible_all: boolean;
}

interface EnrollmentSelection {
  id: string;
  employee_id: string;
  employee_email?: string;
  selections: Array<{
    plan_name: string;
    plan_type: string;
    tier: string;
    employee_cost: number;
  }>;
  total_employee_cost: number;
  total_employer_cost: number;
  esigned: boolean;
  submitted_at?: string;
}

interface BooksSync {
  synced: boolean;
  employee_count: number;
  monthly_employer_cost: number;
  monthly_employee_deductions: number;
  synced_at: string;
}

// ─── Plan Type Badge ──────────────────────────────────────────

const PLAN_COLORS: Record<string, string> = {
  health: 'bg-green-500/20 text-green-400',
  dental: 'bg-blue-500/20 text-blue-400',
  vision: 'bg-purple-500/20 text-purple-400',
  '401k': 'bg-amber-500/20 text-amber-400',
  fsa: 'bg-cyan-500/20 text-cyan-400',
  hsa: 'bg-teal-500/20 text-teal-400',
  life_insurance: 'bg-red-500/20 text-red-400',
  disability: 'bg-zinc-500/20 text-zinc-400',
};

// ─── Create Plan Modal ────────────────────────────────────────

function CreatePlanModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    name: '',
    plan_type: 'health' as BenefitPlan['plan_type'],
    provider: '',
    description: '',
    active: true,
    coverage_tiers: [
      { tier: 'employee' as const, employee_cost: 0, employer_cost: 0 },
      { tier: 'employee_spouse' as const, employee_cost: 0, employer_cost: 0 },
      { tier: 'family' as const, employee_cost: 0, employer_cost: 0 },
    ] as CoverageTier[],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateTier(index: number, field: 'employee_cost' | 'employer_cost', value: number) {
    setForm(f => ({
      ...f,
      coverage_tiers: f.coverage_tiers.map((t, i) => i === index ? { ...t, [field]: value } : t),
    }));
  }

  async function save() {
    if (!form.name.trim() || !form.provider.trim()) { setError('Name and provider are required'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/people/benefits/plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to create plan');
      onCreated();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const TIER_LABELS: Record<string, string> = {
    employee: 'Employee Only',
    employee_spouse: 'Employee + Spouse',
    family: 'Family',
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Add Benefit Plan</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground block mb-1">Plan Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Blue Shield PPO Gold"
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Plan Type</label>
            <select value={form.plan_type} onChange={e => setForm(f => ({ ...f, plan_type: e.target.value }))}
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground">
              {['health', 'dental', 'vision', '401k', 'fsa', 'hsa', 'life_insurance', 'disability'].map(t => (
                <option key={t} value={t}>{t.replace('_', ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Provider *</label>
            <input value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
              placeholder="e.g. Blue Shield, Fidelity"
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">Coverage Tiers (monthly cost)</h4>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground px-1">
              <span>Tier</span><span>Employee Cost/mo</span><span>Employer Cost/mo</span>
            </div>
            {form.coverage_tiers.map((tier, i) => (
              <div key={tier.tier} className="grid grid-cols-3 gap-2 items-center">
                <span className="text-xs text-foreground">{TIER_LABELS[tier.tier]}</span>
                <input
                  type="number" min="0" value={tier.employee_cost}
                  onChange={e => updateTier(i, 'employee_cost', parseFloat(e.target.value) || 0)}
                  className="bg-surface-3 border border-border rounded-lg px-2 py-1.5 text-sm text-foreground"
                />
                <input
                  type="number" min="0" value={tier.employer_cost}
                  onChange={e => updateTier(i, 'employer_cost', parseFloat(e.target.value) || 0)}
                  className="bg-surface-3 border border-border rounded-lg px-2 py-1.5 text-sm text-foreground"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          <button onClick={() => void save()} disabled={saving}
            className="px-4 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Add Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function BenefitsPage() {
  const [activeTab, setActiveTab] = useState<'plans' | 'enrollment' | 'selections'>('plans');
  const [plans, setPlans] = useState<BenefitPlan[]>([]);
  const [periods, setPeriods] = useState<EnrollmentPeriod[]>([]);
  const [selections, setSelections] = useState<EnrollmentSelection[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [lastSync, setLastSync] = useState<BooksSync | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [showCreatePeriod, setShowCreatePeriod] = useState(false);
  const [periodForm, setPeriodForm] = useState({ name: '', start_date: '', end_date: '', effective_date: '' });

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/benefits/plans');
      if (res.ok) { const d = await res.json() as { plans: BenefitPlan[] }; setPlans(d.plans ?? []); }
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  const loadPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/benefits/enrollment/open-periods');
      if (res.ok) { const d = await res.json() as { periods: EnrollmentPeriod[] }; setPeriods(d.periods ?? []); }
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  const loadSelections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/benefits/enrollment/selections');
      if (res.ok) {
        const d = await res.json() as { selections: EnrollmentSelection[] };
        setSelections(d.selections ?? []);
      }
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'plans') void loadPlans();
    if (activeTab === 'enrollment') void loadPeriods();
    if (activeTab === 'selections') void loadSelections();
  }, [activeTab, loadPlans, loadPeriods, loadSelections]);

  async function syncToBooks() {
    setSyncing(true);
    try {
      const res = await fetch('/api/people/benefits/books-sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      if (res.ok) setLastSync(await res.json() as BooksSync);
    } catch { /* silent */ } finally { setSyncing(false); }
  }

  async function createPeriod() {
    if (!periodForm.name || !periodForm.start_date || !periodForm.end_date || !periodForm.effective_date) return;
    try {
      const res = await fetch('/api/people/benefits/enrollment/open-periods', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...periodForm, eligible_all: true }),
      });
      if (res.ok) { setShowCreatePeriod(false); setPeriodForm({ name: '', start_date: '', end_date: '', effective_date: '' }); void loadPeriods(); }
    } catch { /* silent */ }
  }

  const STATUS_COLORS: Record<string, string> = {
    open: 'bg-green-500/20 text-green-400',
    upcoming: 'bg-blue-500/20 text-blue-400',
    closed: 'bg-zinc-500/20 text-zinc-400',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Heart className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">Benefits Administration</h1>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'selections' && (
            <button onClick={() => void syncToBooks()} disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-3 hover:bg-surface-2 text-sm text-foreground disabled:opacity-50">
              <DollarSign className="w-3.5 h-3.5" />
              {syncing ? 'Syncing...' : 'Sync to Books'}
            </button>
          )}
          {activeTab === 'plans' && (
            <button onClick={() => setShowCreatePlan(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm">
              <Plus className="w-3.5 h-3.5" /> Add Plan
            </button>
          )}
          {activeTab === 'enrollment' && (
            <button onClick={() => setShowCreatePeriod(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm">
              <Plus className="w-3.5 h-3.5" /> New Period
            </button>
          )}
          <button onClick={() => {
            if (activeTab === 'plans') void loadPlans();
            if (activeTab === 'enrollment') void loadPeriods();
            if (activeTab === 'selections') void loadSelections();
          }} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-3">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {lastSync && (
        <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${lastSync.synced ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`}>
          {lastSync.synced ? <CheckCircle className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
          {lastSync.synced ? `Synced to Books — ${lastSync.employee_count} employees, $${lastSync.monthly_employer_cost.toFixed(2)}/mo employer cost` : 'Books API not configured — local record updated'}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-3 rounded-lg p-1 w-fit">
        {[
          { id: 'plans' as const, label: 'Benefit Plans', icon: Heart },
          { id: 'enrollment' as const, label: 'Open Enrollment', icon: Calendar },
          { id: 'selections' as const, label: 'Employee Selections', icon: Users },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm transition-colors ${activeTab === tab.id ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground'}`}>
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Plans */}
      {activeTab === 'plans' && (
        <div className="space-y-3">
          {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : plans.length === 0 ? (
            <div className="glass rounded-xl p-12 text-center">
              <Heart className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No benefit plans yet. Click "Add Plan" to get started.</p>
            </div>
          ) : (
            plans.map(plan => (
              <div key={plan.id} className="glass rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground">{plan.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${PLAN_COLORS[plan.plan_type] ?? 'bg-zinc-500/20 text-zinc-400'}`}>
                        {plan.plan_type.toUpperCase()}
                      </span>
                      {!plan.active && <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-500/20 text-zinc-400">Inactive</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{plan.provider}</p>
                  </div>
                </div>
                {plan.coverage_tiers.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {plan.coverage_tiers.map(tier => (
                      <div key={tier.tier} className="bg-surface-3 rounded-lg p-2">
                        <p className="text-muted-foreground mb-1">{tier.tier.replace(/_/g, ' ')}</p>
                        <p className="text-foreground font-medium">${tier.employee_cost}/mo</p>
                        <p className="text-zinc-500">+${tier.employer_cost} employer</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Enrollment Periods */}
      {activeTab === 'enrollment' && (
        <div className="space-y-3">
          {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : periods.length === 0 ? (
            <div className="glass rounded-xl p-12 text-center">
              <Calendar className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No enrollment periods yet.</p>
            </div>
          ) : (
            periods.map(period => (
              <div key={period.id} className="glass rounded-xl p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground">{period.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[period.status]}`}>{period.status}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(period.start_date).toLocaleDateString()} – {new Date(period.end_date).toLocaleDateString()}
                      {' · '}Effective {new Date(period.effective_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-foreground">{period.completion_count}</p>
                    <p className="text-xs text-muted-foreground">completed</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Employee Selections */}
      {activeTab === 'selections' && (
        <div className="glass rounded-xl overflow-hidden">
          {loading ? <p className="p-4 text-sm text-muted-foreground">Loading...</p> : selections.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No employee selections yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-2">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Employee</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Plans</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Employee Cost/mo</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Employer Cost/mo</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground">Signed</th>
                </tr>
              </thead>
              <tbody>
                {selections.map(sel => (
                  <tr key={sel.id} className="border-b border-border/50 hover:bg-surface-3/50">
                    <td className="px-4 py-3 text-foreground">{sel.employee_email ?? sel.employee_id}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {sel.selections.map((s, i) => (
                          <span key={i} className={`px-1.5 py-0.5 rounded text-xs ${PLAN_COLORS[s.plan_type] ?? 'bg-zinc-500/20 text-zinc-400'}`}>
                            {s.plan_name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-foreground">${sel.total_employee_cost.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-foreground">${sel.total_employer_cost.toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      {sel.esigned ? <CheckCircle className="w-4 h-4 text-green-400 mx-auto" /> : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create Plan Modal */}
      {showCreatePlan && (
        <CreatePlanModal onClose={() => setShowCreatePlan(false)} onCreated={() => void loadPlans()} />
      )}

      {/* Create Period Modal */}
      {showCreatePeriod && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Create Enrollment Period</h3>
              <button onClick={() => setShowCreatePeriod(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            {[
              { key: 'name', label: 'Period Name', type: 'text', placeholder: 'e.g. 2026 Open Enrollment' },
              { key: 'start_date', label: 'Start Date', type: 'date', placeholder: '' },
              { key: 'end_date', label: 'End Date', type: 'date', placeholder: '' },
              { key: 'effective_date', label: 'Effective Date', type: 'date', placeholder: '' },
            ].map(field => (
              <div key={field.key}>
                <label className="text-xs text-muted-foreground block mb-1">{field.label}</label>
                <input
                  type={field.type}
                  value={periodForm[field.key as keyof typeof periodForm]}
                  onChange={e => setPeriodForm(f => ({ ...f, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
                />
              </div>
            ))}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreatePeriod(false)} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
              <button onClick={() => void createPeriod()} className="px-4 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
